<template>
  <div
    class="input"
    :class="[
      `input--${theme}`,
      `input--${variant}`,
      `input--${labelVariant}-label`,
      { 'input--with-label': isLabelAvailable },
      { 'input--optional': !isRequired },
      { 'input--bold-label': isLabelBold },
    ]"
  >
    <label
      class="input__label"
      :class="[
        `input__label--${labelVariant}`,
        { 'input__label--bold': isLabelBold },
        { 'input__label--hidden': !isLabelVisible },
      ]"
      :for="idText"
      v-data-test-id="'label'"
      v-html="labelText"
    />
    <span v-if="isCharacterCountActive" class="input__characters-counter">
      {{ charactersCount }}/{{ maxCharacters }}
    </span>
    <transition name="fade">
      <span
        v-show="isPlaceholderVisible"
        class="input__placeholder"
        :class="{ 'input__placeholder--textarea': isTextArea }"
        @click="focus"
        v-data-test-id="'placeholder'"
        v-html="placeholderText"
      />
    </transition>
    <component
      :is="inputTag"
      ref="input"
      :value="isTextArea ? false : value"
      :type="isTextArea ? false : renderedType"
      :id="idText"
      :name="nameText"
      placeholder=""
      :required="isRequired"
      :disabled="isDisabled"
      :autocomplete="autocompleteString"
      :maxlength="isCharacterCountActive ? maxCharacters : false"
      :class="['input__field', { 'input__field--textarea': isTextArea }]"
      v-bind="$attrs"
      v-on="$listeners"
      @input="onValueChange"
      @focus="setFocus(true)"
      @blur="setFocus(false)"
      @animationstart="onAnimationStart"
      v-text="isTextArea ? value : false"
      v-data-test-id="'input'"
    />
    <span v-if="isTypePassword">
      <span
        v-show="isPasswordObscurityToggleVisibile"
        class="input__show-password"
        :class="{
          'icon-eye-opened': isRenderedTypePassword,
          'icon-eye-closed': !isRenderedTypePassword,
          'input__show-password--shifted': isSafari,
        }"
        @click="togglePasswordObscurity"
      ></span>
    </span>
    <slot name="below"></slot>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getHash } from '@/helpers/number'
import propsInput, {
  TYPES,
  INPUT_TAGS,
  LABEL_VARIANTS,
  LABEL_STYLES,
} from '@/components/_shared/Form/Input/props-input'

export default {
  name: 'Input',

  // bind attributes to input (not to rool el) using v-bind="$attrs"
  inheritAttrs: false,

  props: { ...propsInput },

  data() {
    return {
      hasFocus: false,
      isAutofilled: false,
      hash: getHash(),
      renderedType: this.type,
    }
  },

  computed: {
    ...mapGetters({
      isSafari: 'page/isSafari',
    }),

    idText() {
      return this.id || `${this.nameText}-${this.hash}`
    },

    nameText() {
      return this.label?.replace(/\s+/g, '-')?.toLowerCase() || this.id
    },

    placeholderText() {
      if (this.placeholder) return this.placeholder
      return this.labelText + this.optionalText
    },

    labelText() {
      return this.isLabelFixed && this.placeholder ? this.label + this.optionalText : this.label
    },

    optionalText() {
      return !this.isRequired
        ? ` <span class="input__optional-text">${this.$t('checkout.address.optional')}</span>`
        : ''
    },

    hasValue() {
      return !!this.value || this.isAutofilled
    },

    isLabelVisible() {
      return this.hasValue || this.hasFocus || this.isLabelFixed
    },

    isLabelAvailable() {
      return !!this.label
    },

    isLabelFixed() {
      return this.labelVariant === LABEL_VARIANTS.fixed
    },

    isLabelBold() {
      return this.labelStyle === LABEL_STYLES.bold
    },

    isPlaceholderVisible() {
      return !this.hasValue && !this.hasFocus
    },

    autocompleteString() {
      const turnOff = this.autocomplete === 'off'
      // autocomplete "off" is not supported by all browsers
      return turnOff ? this.idText : this.autocomplete
    },

    isPasswordObscurityToggleVisibile() {
      return this.isTypePassword && this.hasValue
    },

    isTypePassword() {
      return this.type === TYPES.password
    },

    isRenderedTypePassword() {
      return this.renderedType === TYPES.password
    },

    isTextArea() {
      return this.inputTag === INPUT_TAGS.textarea
    },

    charactersCount() {
      return this.value?.length
    },
  },

  watch: {
    hasFocus: 'onFocusChange',

    value() {
      // fix to overwrite browser autocomplete value
      if (this.value !== undefined && this.value !== this.$refs.input.value)
        this.$refs.input.value = this.value
    },
  },

  methods: {
    getFieldEl() {
      return this.$refs.input
    },

    focus() {
      this.getFieldEl()?.focus()
    },

    setFocus(hasFocus) {
      this.hasFocus = hasFocus
    },

    onAnimationStart(event) {
      // More details on how to detect if a field gets autofilled:
      // https://medium.com/@brunn/detecting-autofilled-fields-in-javascript-aed598d25da7
      this.isAutofilled = /^autofill/.test(event.animationName)
    },

    onValueChange($event) {
      // this should only be emitted for 'input' event, not for 'change' event
      this.$emit('update:value', $event.target.value)
    },

    onFocusChange() {
      this.$emit('hasFocus', this.hasFocus)
    },

    togglePasswordObscurity() {
      if (this.isTypePassword) {
        this.renderedType = this.renderedType === TYPES.password ? TYPES.text : TYPES.password
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/components/_shared/Form/form-settings';

.input {
  position: relative;
  margin-top: $input-wrapper-margin-top;

  &--with-label {
    margin-top: $input-wrapper-margin-top-with-label-mobile;

    @media ($mq-large) {
      margin-top: $input-wrapper-margin-top-with-label-desktop;
    }
  }

  &--fixed-label {
    margin-top: $input-wrapper-margin-top-with-fixed-label;
  }

  &--bold-label {
    margin-top: 54px;
  }

  &--dark {
    .input__field {
      // BG color should be defined by the element behind the field
      background-color: transparent;
      border-color: $color-white;
      color: $color-white;

      // remove autofill background color
      &:-webkit-autofill {
        animation-name: autofill-dark;
      }
    }

    .input__placeholder {
      color: $color-white;

      &:focus {
        color: transparent;
      }
    }
  }

  &--red,
  &--orange {
    .input__placeholder {
      &:focus {
        color: transparent;
      }
    }
  }

  &--red {
    .input__field {
      border-color: $field-color-red;
    }
  }

  &--orange {
    .input__field {
      border-color: $field-color-orange;
    }
  }

  &--tall {
    .input__field {
      height: 55px;
    }

    .input__placeholder {
      line-height: 1.65;
    }
  }

  &__label {
    @include field-label;

    &--fixed {
      position: absolute;
      font-size: $font-size-smaller;
      top: -16px;
      left: 0;
      background: none;
      padding: 0;
    }

    &--bold {
      top: -32px;
      color: $color-grey-dark-3;
      font-size: $font-size-small;

      /deep/ .input__optional-text {
        color: $color-grey-mid-2;
      }
    }
  }

  &__placeholder {
    position: absolute;
    display: inline-block;
    z-index: 1;
    opacity: 1;
    top: 0;
    left: 0;
    overflow: hidden;
    width: calc(100% - #{$field-padding-right + $field-padding-left});
    height: calc(100% - #{$field-padding-top + $field-padding-bottom});
    margin: $placeholder-padding;
    border: $field-border-width solid transparent;
    cursor: text;
    user-select: none;
    pointer-events: none;
    font-size: $placeholder-font-size;
    font-style: normal;
    line-height: $line-height;
    white-space: nowrap;
    color: $color-grey-mid-2;

    &--textarea {
      white-space: normal;
    }
  }

  &__field {
    @include field-input;
    position: relative;
    z-index: 0;
    display: block;
    width: $field-width;
    padding: $field-padding;

    // remove autofill background color
    &:-webkit-autofill {
      animation-name: autofill;
      animation-fill-mode: both;
    }

    &:not(:-webkit-autofill) {
      animation-name: cancel-autofill;
    }

    &--textarea {
      min-width: 100%;
      max-width: 100%;
      min-height: space(12);
      height: space(12 * 3);
      max-height: space(12 * 6);
    }
  }

  &__show-password {
    z-index: 1;
    position: absolute;
    top: $field-height / 2;
    right: 50px;
    transform: translateY(-50%);
    height: 14px;
    width: 24px;
    margin: 0;
    line-height: 15px;
    font-size: 26px;
    cursor: pointer;

    &--shifted {
      @media ($mq-large) {
        right: 70px;
      }
    }
  }

  &__characters-counter {
    position: absolute;
    top: -30px;
    right: 0;
    font-size: $font-size-smaller;
    color: $color-grey-mid-2;
  }
}

.fade-enter-active {
  transition: $label-transition-delayed;
  transition-delay: $label-transition-delay;
}

.fade-leave-active {
  transition: $label-transition;
  transition-delay: 0s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@keyframes autofill {
  to {
    background-color: transparent;
  }
}

@keyframes autofill-dark {
  to {
    background-color: transparent;
    color: $color-white;
  }
}

// To make sure animationstart can be fired again if needed
@keyframes cancel-autofill {
  to {
  }
}
</style>
